import { Vector3 } from "three";
import { CancellationToken } from "../../../../../../../composition/CancellationToken";
import { TweenUtils } from "../../../../../../tween/TweenUtils";
import { GameResourcesPackage } from "../../../../resources/GameResourcesPackage";
import { AudioController } from "../../../AudioController";
import { IProjectileContainer } from "../IProjectileContainer";
import { BaseMole } from "./BaseMole";
import { AttackState } from "./states/AttackState";
import { DieState } from "./states/DieState";
import { FreezeState } from "./states/FreezeState";
import { IdleTimeoutState } from "./states/IdleTimeoutState";
import { IMoleState } from "./states/IMoleState";
import { Character } from "../../character/Character";
import { HatType } from "../../../../models/data/HatType";
import { DigInState } from "./states/DigInState";
import { DigOutState } from "./states/DigOutState";
import { GameController } from "../../../../controllers/GameController";
import { BatchedRenderer } from "three.quarks";

export class AttackingMole extends BaseMole {
    private flyCancellation: CancellationToken;

    public constructor(
        gameResources: GameResourcesPackage,
        projectileContainer: IProjectileContainer,
        particlesRenderer: BatchedRenderer,
        character: Character,
        audioController: AudioController,
        gameController: GameController,
        fieldIndex: number
    ) {
        super(
            fieldIndex,
            gameResources,
            projectileContainer,
            particlesRenderer,
            character,
            audioController,
            gameController,
            100,
            Math.random() < 0.5 ? 1500 : 2000
        );

        const dieState = new DieState(this, this.animationController);
        const freezeState = new FreezeState(gameResources, particlesRenderer, this, this.animationController, dieState);
        const digInState = new DigInState(this, this.animationController, dieState, freezeState);
        const attackState: IMoleState = new AttackState(this, this.animationController, digInState, dieState, freezeState, 3);
        const idleBeforeAttackState = new IdleTimeoutState(this, this.animationController, attackState, dieState, freezeState, 3000);
        const digOutState = new DigOutState(this, this.animationController, idleBeforeAttackState, dieState, freezeState);
        this.switchState(digOutState);
    }

    public override destroy(): void {
        this.flyCancellation?.cancel();
        super.destroy();
    }

    public async sendFlying(startPosition: Vector3, endPosition: Vector3, initialRotation: number): Promise<void> {
        this.flyCancellation = new CancellationToken();
        this.position.copy(startPosition);
        this.moleObject.rotation.y = initialRotation;
        const positionTween = TweenUtils.tweenTo(this.position, endPosition, 1000, this.flyCancellation);
        const rotationTween = TweenUtils.tweenTo(this.moleObject.rotation, { x: 0, y: Math.PI, z: 0 }, 1000, this.flyCancellation);
        await Promise.all([positionTween, rotationTween]);
        this.flyCancellation = null;
    }

    protected override resolveHatType(): HatType {
        if (this.attackDelay < 2000) {
            return HatType.Pirate;
        }
        return HatType.Cylinder;
    }
}